import { fetchAuthSession } from 'aws-amplify/auth';

import config, { HAS_API_PROXY } from '../../config/env';
import { BASEURL } from '../endpoints';
import { AuthHeader, OrganizationIDHeader } from '../interfaces';
import { getAWSAmplifyRegion } from '@/api';

/* eslint-disable camelcase */

/** Amplify v6 REST endpoint config shape (`API.REST[name]`). */
interface AmplifyRestEndpointConfig {
    endpoint: string;
    region: string;
    service: string;
}

/**
 * v6 REST config map keyed by API name. IAM/SigV4 signing is applied
 * automatically by the REST client for any request without an `Authorization`
 * header, using `service` ('execute-api') and `region` below.
 */
const getAmplifyAPIEndpoints = (): Record<string, AmplifyRestEndpointConfig> => {
    const region = getAWSAmplifyRegion(config.cognito.IDENTITY_POOL_ID);
    const endpoints: Record<string, AmplifyRestEndpointConfig> = {
        [BASEURL.backend()]: {
            endpoint: config.API_URL,
            region,
            service: 'execute-api',
        },
        [BASEURL.localhost()]: {
            endpoint: 'https://localhost:3001',
            region,
            service: 'execute-api',
        },
    };
    if (HAS_API_PROXY) {
        endpoints[BASEURL.backendProxy()] = {
            endpoint: config.API_PROXY.API_URL,
            region,
            service: 'execute-api',
        };
    }
    return endpoints;
};

/**
 * v6 `libraryOptions.API.REST.headers`. Replaces the v5 per-endpoint
 * `custom_header`. Only the proxy endpoint gets bearer auth headers; adding an
 * `Authorization` header here disables IAM signing for that endpoint (exactly
 * the v5 behaviour), while the IAM-signed `specterx`/`localhost` endpoints send
 * no extra headers.
 */
export const getApiRestHeaders = async (
    { apiName }: { apiName: string },
): Promise<Record<string, string>> => {
    if (apiName !== BASEURL.backendProxy()) {
        return {};
    }
    try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken;
        if (!idToken) {
            throw new Error('baseHeaders error');
        }
        const headers: Partial<AuthHeader & OrganizationIDHeader> = {
            Authorization: `Bearer ${idToken.toString()}`,
            SpecterxUserId: idToken.payload.sub as string,
        };
        return headers as Record<string, string>;
    } catch {
        throw new Error('baseHeaders error');
    }
};

export default getAmplifyAPIEndpoints;
